import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router';

import { t } from '@sm/intl';
import { StaticContext } from '@sm/webassets';
import { Typography } from '@wds/typography';
import { Box } from '@wds/box';
import { IconArrowRight } from '@wds/icons';

import { formatCostInLocaleCurrency, getCoreCostFromSkus } from '~app/pages/Pricing/lib/lib-helpers';
import useTrackingDetails from '~pricing/hooks/useTrackingDetails';
import { COMPARISON_PACKAGES } from '~pricing/lib/constants';
import { PackageIds, Packages } from '~shared/constants/pricing';
import { NavRouteParams } from '~pricing/components/Navigation/types';
import { PackageType } from '~shared/types/pricing/package';
import { usePackages } from '~shared/hooks/usePackages';
import { SummaryFooterPackages, SummaryDisclaimer } from './types';

import FAQ from './components/FAQ';
import FAQAnswer from './components/FAQAnswer';
import disclaimers, { LegaleseDisclaimer } from './config/disclaimers';

import usePricingFooterStyles from './useStyles';

import PricingFeatures from './PricingFeatures';
import COPY from './copy';
import FAQPackagePrice from './components/FAQPackagePrice';

// List of country codes where we allow the educational discount
const EDU_COUNTRIES = ['US', 'GB'];

// List of packages to not display in the footer FAQ
const HIDDEN_FAQ_PACKAGES = [Packages.StandardAnnual, Packages.Enterprise];

export default function PricingFooter({ packages }: SummaryFooterPackages): React.ReactElement {
  const {
    user,
    environment: { countryCode },
  } = useContext(StaticContext);
  const { overageCost, getPackageById } = usePackages();
  const { canonicalPath, subPath } = useParams<NavRouteParams>();
  const { pageUtSource } = useTrackingDetails();
  const { Audience, Disclaimers, Disclaimer, Footer, GetStarted, More, PricingList, Col, SeeAllLink } =
    usePricingFooterStyles();
  const displayPackages = useMemo(() => {
    const filteredPackages = packages.filter(pkg => !HIDDEN_FAQ_PACKAGES.includes(pkg.packageName));
    // Reverse the packages cost FAQ list for EDU page.
    if (canonicalPath === PackageType.Educational) {
      filteredPackages.reverse();
    }
    return filteredPackages;
  }, [packages, canonicalPath]);

  // for the sales form link
  const utSource2 = pageUtSource?.includes('summary') ? 'summary' : 'details';
  const utSource3 = `${utSource2}_bottom_${user.isAuthenticated ? 'li' : 'lo'}`;

  const path = canonicalPath || PackageType.Teams;
  const displayEduFaq = path !== PackageType.Educational && EDU_COUNTRIES.includes(countryCode);
  // we hide the teams FAQ only on teams details and, subsequently, canonical details
  const displayTeamsDiscountFAQ =
    !(path === PackageType.Teams && subPath) && path !== 'details' && path !== PackageType.Educational;

  // this value represents the lowest percentage discount of all teams <-> individual packages,
  // rounded down to the nearest ten. it's used for legalese underneath the teams packages
  const teamsDiscountPercent = useMemo<number>(() => {
    const discountPercent = Object.keys(COMPARISON_PACKAGES).reduce<number>((minimumPercent, teamPackageName) => {
      let newMinimumPercent = minimumPercent;

      const comparisonPackageName = COMPARISON_PACKAGES[teamPackageName as Packages];
      if (comparisonPackageName) {
        const teamPricingPackage = getPackageById(PackageIds[teamPackageName as Packages]);
        const comparisonPricingPackage = getPackageById(PackageIds[comparisonPackageName]);
        const teamSku = getCoreCostFromSkus(teamPricingPackage?.skuCost);
        const comparisonSku = getCoreCostFromSkus(comparisonPricingPackage?.skuCost);

        if (teamSku && comparisonSku) {
          const packagePercent = Math.floor(100 - (teamSku.cost / comparisonSku.cost) * 100);
          if (packagePercent < newMinimumPercent) {
            newMinimumPercent = packagePercent;
          }
        }
      }

      return newMinimumPercent;
    }, 100);

    return Math.floor(discountPercent / 10) * 10;
  }, [getPackageById]);

  return (
    <footer className={Footer} data-testid="PricingFooter">
      <ul className={Disclaimers}>
        {disclaimers[path as keyof SummaryDisclaimer]?.map((disclaimer: string) => {
          if (disclaimer === LegaleseDisclaimer.EnterpriseAddons && subPath) {
            return;
          }
          let formattedOverageCost = null;
          if (disclaimer === LegaleseDisclaimer.OverageCost && overageCost) {
            formattedOverageCost = formatCostInLocaleCurrency(overageCost, 2);
          }

          return (
            <li className={Disclaimer} key={disclaimer} data-testid={`PricingFooter__${disclaimer}`}>
              <Typography variant="bodySm">
                {t(COPY[disclaimer as keyof typeof COPY], {
                  overageCost: formattedOverageCost,
                  teamsMinimumDiscount: teamsDiscountPercent,
                })}
              </Typography>
            </li>
          );
        })}
      </ul>

      {/**
       *
       * The Audience portion of the footer should only be rendered on Summary pages.
       * subPath is required to to determine if we're on the details page or not.
       * path can be either 'educational' (details view) or 'teams', or 'individual' (summary view)
       */}
      {path !== PackageType.Educational && !subPath && (
        <div className={Audience}>
          <PricingFeatures />

          {!user.isAuthenticated && (
            <Box className={GetStarted} data-testid="PricingFooter__SignUp">
              <Typography component="p" variant="body" align="center">
                {t(COPY.BASICS)} <Link to="/user/sign-up/?ut_source=pricing_page">{t(COPY.GET_STARTED)}</Link>
              </Typography>
            </Box>
          )}

          <Typography component="h5" align="center">
            <Link className={SeeAllLink} to={`/pricing/${path}/details?ut_source=${pageUtSource}`}>
              {t(COPY.ALL_FEATURES)} <IconArrowRight size="sm" title="Arrow Right" />
            </Link>
          </Typography>
        </div>
      )}

      <Box className={More}>
        <Typography component="h3" variant="sectionTitle" align="center">
          {t(COPY.MORE)}
        </Typography>
      </Box>

      <Box className={PricingList}>
        <Box className={Col}>
          <FAQ heading={t(COPY.SURVEYMONKEY_COST)}>
            {displayPackages.map(({ packageName, details, skuCost }) => (
              <FAQPackagePrice
                packageName={packageName}
                displayName={details.displayName}
                skuCosts={skuCost}
                key={`PricingFooter__Faq${packageName}`}
                countryCode={countryCode}
              />
            ))}
          </FAQ>
        </Box>

        <Box className={Col}>
          {displayEduFaq && (
            <FAQ data-testid="PricingFooter__EduPricing" heading={t(COPY.SPECIAL_PRICING_FOR_EDUCATORS_FAQ)}>
              <FAQAnswer>
                {t(COPY.SPECIAL_PRICING_FOR_EDUCATORS_ANSWER)}{' '}
                <Link to={`/pricing/educational/?ut_source=${pageUtSource}`}>
                  {t(COPY.SEE_PRICING)} <IconArrowRight size="sm" title="Arrow Right" />
                </Link>
              </FAQAnswer>
            </FAQ>
          )}

          {displayTeamsDiscountFAQ && (
            <FAQ heading={t(COPY.DISCOUNTS_FOR_TEAMS_FAQ)}>
              <FAQAnswer>
                {t(COPY.DISCOUNTS_FOR_TEAMS_ANSWER, { teamsMinimumDiscount: teamsDiscountPercent })}{' '}
                <Link to={`/pricing/teams/details/?ut_source=${pageUtSource}`}>
                  {t(COPY.SEE_PRICING)} <IconArrowRight size="sm" title="Arrow Right" />
                </Link>
              </FAQAnswer>
            </FAQ>
          )}

          <FAQ heading={t(COPY.CORPORATE_PLANS_FAQ)}>
            <FAQAnswer>
              {t(COPY.CORPORATE_PLANS_ANSWER)}{' '}
              <Link to={`/mp/contact-sales/?ut_source=pricing_page&ut_source2=${utSource2}&ut_source3=${utSource3}`}>
                {t(COPY.CONTACT_SALES)} <IconArrowRight size="sm" title="Arrow Right" />
              </Link>
            </FAQAnswer>
          </FAQ>
        </Box>
      </Box>
    </footer>
  );
}
